<script setup>
import { ref, computed, watch } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  utilisateurs:   { type: Array,   default: () => [] },
  total_count:    { type: Number,  default: 0 },
  actifs_count:   { type: Number,  default: 0 },
  inactifs_count: { type: Number,  default: 0 },
  can_create:     { type: Boolean, default: false },
  show_all:       { type: Boolean, default: false },
})

const openDropdown = ref(null)
function toggleDropdown(id) {
  openDropdown.value = openDropdown.value === id ? null : id
}

// Search + pagination + sort
const search      = ref('')
const pageSize    = ref(10)
const currentPage = ref(1)
const sortKey     = ref('nom')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.utilisateurs
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(u =>
      u.email.toLowerCase().includes(q) ||
      u.nom.toLowerCase().includes(q) ||
      u.prenom.toLowerCase().includes(q) ||
      u.role_nom.toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages  = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

// Export
function copyTable() {
  const header = ['Email', 'Nom', 'Prénom', 'Rôle', 'État'].join('\t')
  const rows = filtered.value.map(u =>
    [u.email, u.nom, u.prenom, u.role_nom, u.actif ? 'Actif' : 'Inactif'].join('\t')
  )
  navigator.clipboard.writeText([header, ...rows].join('\n'))
    .then(() => alert('Tableau copié dans le presse-papiers'))
    .catch(() => alert('Impossible de copier'))
}

function exportCsv() {
  const header = 'Email,Nom,Prénom,Rôle,État'
  const rows = filtered.value.map(u =>
    [`"${u.email}"`, `"${u.nom}"`, `"${u.prenom}"`, `"${u.role_nom}"`, u.actif ? 'Actif' : 'Inactif'].join(',')
  )
  const csv  = '\ufeff' + [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: 'utilisateurs.csv' })
  a.click()
  URL.revokeObjectURL(url)
}

function printTable() { window.print() }
</script>

<template>
  <AppLayout>
    <!-- TopBar left: search -->
    <template #topbar-left>
      <div class="relative w-80 group">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher un utilisateur..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8" @click="openDropdown = null">

      <!-- Page header -->
      <div class="flex justify-between items-end mb-6">
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">Gestion des Utilisateurs</h1>
          <div class="flex items-center gap-4 text-[10px] font-black text-outline uppercase tracking-widest">
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-tertiary"></span>
              {{ actifs_count }} Actifs
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-outline/50"></span>
              {{ inactifs_count }} Inactifs
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <!-- Sélecteur par page -->
          <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
            <select
              v-model="pageSize"
              class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
            >
              <option :value="10">10 Utilisateurs</option>
              <option :value="25">25 Utilisateurs</option>
              <option :value="50">50 Utilisateurs</option>
            </select>
          </div>
          <!-- Bouton Nouveau -->
          <a
            v-if="can_create"
            href="/utilisateurs/new"
            class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
          >
            <span class="material-symbols-outlined text-sm">person_add</span>
            Nouvel Utilisateur
          </a>
        </div>
      </div>

      <!-- Onglets -->
      <!-- <div class="flex items-center gap-1 mb-6">
        <div class="px-6 py-4 text-xs font-black uppercase tracking-widest rounded-sm bg-primary text-on-primary">
          Liste des utilisateurs
        </div>
      </div> -->

      <!-- Onglets -->
      <div class="flex items-center gap-1 mb-6">
        <a
          href="/utilisateurs"
          :class="[
            'px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm transition-all',
            !show_all ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10'
          ]"
        >Liste active</a>
        <a
          href="/utilisateurs?all=true"
          :class="[
            'px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm transition-all',
            show_all ? 'bg-primary text-on-primary' : 'text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10'
          ]"
        >Liste complète</a>
      </div>

      <!-- Table -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
        <table class="w-full text-left border-collapse print-table">
          <thead>
            <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
              <th @click="toggleSort('email')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Email<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'email' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th @click="toggleSort('nom')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-48 cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Nom Complet<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'nom' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th @click="toggleSort('role_nom')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-40 cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Rôle<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'role_nom' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th @click="toggleSort('actif')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-28 cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">État<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'actif' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline text-right w-20">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-outline-variant/10">
            <tr
              v-for="(u, i) in paginated"
              :key="u.id"
              :class="[
                'group hover:bg-surface-container-highest/20 transition-colors',
                !u.actif ? 'opacity-60' : '',
                i % 2 === 1 ? 'bg-surface-container-low/20' : '',
              ]"
            >
              <!-- Email -->
              <td class="px-6 py-4 align-top">
                <a :href="`/utilisateurs/${u.id}`" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">
                  {{ u.email }}
                </a>
              </td>
              <!-- Nom Complet -->
              <td class="px-6 py-4 align-top">
                <div class="font-bold text-on-surface text-sm uppercase tracking-tight">{{ u.nom }} {{ u.prenom }}</div>
              </td>
              <!-- Rôle -->
              <td class="px-6 py-4 align-top">
                <span class="inline-flex items-center px-2.5 py-1 rounded bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-widest text-primary">
                  {{ u.role_nom || '—' }}
                </span>
              </td>
              <!-- État -->
              <td class="px-6 py-4 align-top">
                <div
                  v-if="u.actif"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-tertiary/10 border border-tertiary/20"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-tertiary animate-pulse"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-tertiary">Actif</span>
                </div>
                <div
                  v-else
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-surface-container-highest border border-outline-variant/20"
                >
                  <span class="w-1.5 h-1.5 rounded-full bg-outline/50"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-outline">Inactif</span>
                </div>
              </td>
              <!-- Actions -->
              <td class="px-6 py-4 align-top text-right">
                <div class="relative inline-block">
                  <button
                    @click.stop="toggleDropdown(u.id)"
                    class="inline-flex p-2 text-outline hover:text-primary hover:bg-primary/10 rounded transition-all"
                  >
                    <span class="material-symbols-outlined text-lg">edit_note</span>
                  </button>
                  <div
                    v-if="openDropdown === u.id"
                    class="absolute right-0 top-full z-50 mt-1 bg-surface-container-high border border-outline-variant/20 rounded-lg shadow-xl min-w-[160px] py-1"
                  >
                    <a :href="`/utilisateurs/${u.id}/edit`" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">edit_note</span>
                      Modifier
                    </a>
                  </div>
                </div>
              </td>
            </tr>
            <!-- Empty state -->
            <tr v-if="paginated.length === 0">
              <td colspan="5" class="px-6 py-16 text-center text-outline text-sm">
                <span class="material-symbols-outlined text-4xl block mb-3 opacity-30">person_search</span>
                Aucun utilisateur trouvé{{ search ? ` pour "${search}"` : '' }}.
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination footer -->
        <div class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, filtered.length) }}
            sur {{ filtered.length }} utilisateur{{ filtered.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage--"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button
                v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary'
                ]"
                @click="currentPage = p"
              >{{ p }}</button>
              <span
                v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm"
              >…</span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage++"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Boutons export -->
      <div class="flex items-center gap-3 mt-5 no-print">
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="exportCsv"
        >
          <span class="material-symbols-outlined text-base">download</span>
          Excel / CSV
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="printTable"
        >
          <span class="material-symbols-outlined text-base">print</span>
          Imprimer
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="copyTable"
        >
          <span class="material-symbols-outlined text-base">content_copy</span>
          Copier
        </button>
      </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>

<style>
@media print {
  .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
  .print-table th, .print-table td { color: black !important; border: 1px solid #ccc !important; }
}
</style>
